import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";
import arrowIcon from "../../../assets/images/Vector_btn.png";

import LottieAnimation from "../../../assets/json/sezione-04-popup-01-it.json";

import Img01 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-01.svg";
import Img02 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-02.svg";
import Img03 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-03.svg";
import Img04 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-04.svg";
import Img05 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-05.svg";
import Img06 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-06.svg";
import Img07 from "../../../assets/images/numeri_ico/sezione-04-popup-01-ico-07.svg";

/* placeholder icona */
const PLACEHOLDER_SVG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="%230E3761" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="14" rx="2" ry="2"/><path d="M8 21h8M12 17v4"/></svg>';

/** Donut mobile con contenuto interno + rotazione spicchio */
function DonutMobile({
  percent = 30,
  value = "0,0",
  label = "",
  color = "#0E3761",
  img = PLACEHOLDER_SVG,
  angleOffset = 0,
}) {
  const data = [
    { name: "val", value: percent },
    { name: "rest", value: 100 - percent },
  ];
  const start = 90 + angleOffset;
  const end = start - 360;

  return (
    <div className="relative w-[260px] h-[260px] mx-auto my-6">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            innerRadius="85%"
            outerRadius="98%"
            startAngle={start}
            endAngle={end}
            stroke="none"
            isAnimationActive={false}
            cornerRadius={2}
            padAngle={1.5}
          >
            <Cell fill={color} />
            <Cell fill="#DDE7F2" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* contenuto interno al donut */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-3 pointer-events-none">
        <img src={img} alt="" className="w-10 h-10 mb-2 opacity-90" />
        <div className="flex items-start gap-2 w-[80%]">
          <div className="flex flex-col items-end min-w-[80px]" style={{ fontSize: 24, lineHeight: "1em" }}>
            <div className="text-[#0E3761] font-bold text-right">
              {value}
              <br />
              <span style={{ fontSize: 14 }}>mln €</span>
            </div>
            <div className="text-[#0E3761] font-bold leading-[1.2em]" style={{ fontSize: 14 }}>
              ({String(percent).replace(".", ",")}%)
            </div>
          </div>
          <div className="flex-1 text-left text-[#0E3761] font-bold leading-[1.2em]" style={{ fontSize: 14 }}>
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- POPUP --------------------------- */
export default function PopupNumeri({ isOpen = true, onClose = () => {} }) {
  const overlayRef = useRef(null);
  const lottieRef = useRef(null);
  const [showSocioBtn, setShowSocioBtn] = useState(false);

  // NEW: stato per il video popup
  const [videoOpen, setVideoOpen] = useState(false);

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Distribuzione del valore", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };
  

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (videoOpen) setVideoOpen(false);
      else onClose?.();
    };
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose, videoOpen]);

  // Mostra il bottone solo al termine della Lottie (desktop)
  useEffect(() => {
    if (!isOpen) return;
    const inst = lottieRef.current;
    if (!inst) return;

    const handleComplete = () => setShowSocioBtn(true);
    try {
      inst.addEventListener("complete", handleComplete);
    } catch {
      const t = setTimeout(() => setShowSocioBtn(true), 3000);
      return () => clearTimeout(t);
    }
    return () => {
      try {
        inst.removeEventListener("complete", handleComplete);
      } catch {}
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
        onMouseDown={(e) => {
          if (e.target === overlayRef.current) onClose?.();
        }}
        role="dialog"
        aria-modal="true"
        aria-label="I nostri numeri — Distribuzione del valore"
      >
        <div
          className="relative w-full md:w-[1280px] bg-white max-h-[90vh] overflow-y-auto overscroll-contain"
          style={{ background: "linear-gradient(135deg, #F5F9FF 0%, #E8F2FF 100%)" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
            <button
              onClick={() => onShare("PopupNumeri")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
              title="Condividi"
              aria-label="Condividi"
            >
              <img src={shareIcon} alt="" className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
              title="Chiudi"
              aria-label="Chiudi"
            >
              <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
            </button>
          </div>

          {/* Contenuto */}
          <div className="px-5 md:px-8 pb-8 -mt-6">
            <h2 className="text-[#0E3761] text-[30px] md:text-[40px] font-bold">I nostri numeri</h2>
            <p className="text-[#0E3761] text-[18px] md:text-[22px] mt-1 mb-0">Distribuzione del valore</p>
            <p className="text-[#0E3761] text-[12px] md:text-[14px] mt-0 italic">*Situazione al 31 Dicembre 2025</p>

            {/* DESKTOP — Lottie + bottone che compare a fine animazione */}
            <div className="hidden mt-2 md:flex flex-col items-center relative w-full md:max-w-[800px] mx-auto">
              <Lottie
                lottieRef={lottieRef}
                animationData={LottieAnimation}
                loop={false}
                autoplay
                className="w-full"
              />

              {showSocioBtn && (
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="bt-secondary my-2 md:my-5 flex items-start gap-2 absolute right-[0%] top-[19.5%] text-left leading-[1em]"
                  style={{ fontSize: "11px", padding: "4px 8px" }}
                  aria-haspopup="dialog"
                  aria-controls="socio-video-modal"
                >
                  <img src={arrowIcon} alt="" className="w-[24px] h-[24px]" />
                  <span>
                    Guarda il video spot
                    <br />
                    REALE PER TE, MUTUA PER TUTTI
                  </span>
                </button>
              )}
            </div>

            {/* MOBILE — lista di donut (Recharts) */}
            <div className="md:hidden mt-4 flex flex-col items-center">

              <h2 className="text-[#2A357E] text-center leading-none">2.160,7 mln € <br/>(100%)</h2>
              <DonutMobile
                percent={1.5}
                value="32,7"
                label="Soci/Assicurati-Clienti (Vantaggi mutualistici)"
                color="#4B79B7"
                img={Img01}
                angleOffset={0}
              />

              <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="bt-secondary my-2 absolute right-[50%] translate-x-[50%] top-[368px] text-[12px]"
                  style={{ fontSize: "10px", padding: "2px 5px", gap:"5px" }}
                  aria-haspopup="dialog"
                  aria-controls="socio-video-modal"
                >
                  <img src={arrowIcon} alt="" className="w-[12px] h-[12px]" />
                  <span className="text-left leading-[1.2]">
                    Guarda il video spot
                    <br />
                    REALE PER TE, MUTUA PER TUTTI
                  </span>
                </button>
              
              <DonutMobile
                percent={17.2}
                value="372,5"
                label="Dipendenti (Costo del personale)"
                color="#0E3761"
                img={Img02}
                angleOffset={-10}
              />
              <DonutMobile
                percent={44.7}
                value="965,0"
                label="Agenti e altri intermediari (Provvigioni e altre spese di acquisizione)"
                color="#0E3761"
                img={Img03}
                angleOffset={-60}
              />
              <DonutMobile
                percent={2.1}
                value="44,5"
                label="Amministrazione Finanziaria (Imposte dirette)"
                color="#0E3761"
                img={Img04}
                angleOffset={130}
              />
              <DonutMobile
                percent={19.1}
                value="411,7"
                label="Collaboratori esterni (Costi e spese generali)"
                color="#87B6E1"
                img={Img05}
                angleOffset={120}
              />
              <DonutMobile
                percent={14.8}
                value="320,5"
                label="Sistema impresa (Utile dell’anno)"
                color="#87B6E1"
                img={Img06}
                angleOffset={45}
              />
              <DonutMobile
                percent={0.6}
                value="13,8"
                label="Collettività (Spese relative a sponsorizzazioni ed erogazioni/contributi)"
                color="#0E3761"
                img={Img07}
                angleOffset={0}
              />
            </div>

            {/* CTA finale */}
            <div className="mt-6 flex justify-center">
              <a href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=22" target="_blank">
                <button
                  className="bt-secondary2 text-sm px-4 py-2"
                >
                  <img
                    src={arrowIconWhite}
                    alt="Arrow"
                    className="inline-block mr-2 w-[20px] h-[20px]"
                  />
                  Consulta il Report - cap. 1 • par. 1.1.3.3
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* VIDEO POPUP */}
      {videoOpen &&
        createPortal(
          <div
            id="socio-video-modal"
            className="fixed inset-0 z-[2600] bg-black/70 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Riproduzione video spot REALE PER TE, MUTUA PER TUTTI"
            onMouseDown={(e) => {
              if (e.currentTarget === e.target) setVideoOpen(false);
            }}
          >
            <div className="relative w-full max-w-[960px]">
              {/* Close */}
              <button
                onClick={() => setVideoOpen(false)}
                className="absolute -top-10 right-0 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/90 bg-white/10 border border-white/20 hover:bg-white/20 transition"
                title="Chiudi video"
                aria-label="Chiudi video"
              >
                <img src={closeIcon} alt="Chiudi" className="h-4 w-4 invert" />
              </button>

              {/* 16:9 wrapper */}
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src="https://www.youtube.com/embed/LB3JPkxnwq8?autoplay=1&rel=0"
                  title="SO’ SOCIO — Spot"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>,
    document.body
  );
}
